import { PayloadAction, createSlice, createSelector } from "@reduxjs/toolkit";
import { NetworkProgram } from "../../API";
import { QueryStatus } from "../../app/types";
import { RootState } from "../../app/store";

interface NetworkProgramsSlice {
  // Key: sorted programIds joined by comma (e.g., "id1,id2")
  // Value: NetworkPrograms for those programIds
  networkProgramsByProgramIds: Record<string, NetworkProgram[]>;
  statusByProgramIds: Record<string, QueryStatus>;
  errorByProgramIds: Record<string, string | undefined>;
}

const initialState: NetworkProgramsSlice = {
  networkProgramsByProgramIds: {},
  statusByProgramIds: {},
  errorByProgramIds: {}
};

export const networkProgramsSlice = createSlice({
  name: "networkPrograms",
  initialState,
  reducers: {
    setNetworkPrograms: (
      state,
      action: PayloadAction<{
        programIds: string[];
        networkPrograms: NetworkProgram[] | null | undefined;
      }>
    ) => {
      const key = [...action.payload.programIds].sort().join(",");
      state.networkProgramsByProgramIds[key] = action.payload.networkPrograms || [];
    },
    setNetworkProgramsStatus: (
      state,
      action: PayloadAction<{
        programIds: string[];
        status: QueryStatus;
      }>
    ) => {
      const key = [...action.payload.programIds].sort().join(",");
      state.statusByProgramIds[key] = action.payload.status;
    },
    setNetworkProgramsError: (
      state,
      action: PayloadAction<{
        programIds: string[];
        error: string | undefined;
      }>
    ) => {
      const key = [...action.payload.programIds].sort().join(",");
      state.errorByProgramIds[key] = action.payload.error;
    }
  }
});

export const {
  setNetworkPrograms,
  setNetworkProgramsStatus,
  setNetworkProgramsError
} = networkProgramsSlice.actions;

// Stable empty array reference to avoid creating new arrays
const EMPTY_ARRAY: NetworkProgram[] = [];

// Base selectors
const selectNetworkProgramsState = (state: RootState) => state.networkPrograms;

// Helper to normalize programIds to a stable key
const normalizeProgramIds = (programIds: string[]): string => {
  return [...programIds].sort().join(",");
};

// Memoized selector factory - returns a memoized selector for specific programIds
export const selectNetworkProgramsByProgramIds = createSelector(
  [
    selectNetworkProgramsState,
    (_state: RootState, programIds: string[]) => normalizeProgramIds(programIds)
  ],
  (networkProgramsState, sortedKey) => {
    const result = networkProgramsState.networkProgramsByProgramIds[sortedKey];
    // Return existing array or stable empty array reference to avoid new array creation
    return result !== undefined ? result : EMPTY_ARRAY;
  }
);

export const selectNetworkProgramsStatus = createSelector(
  [
    selectNetworkProgramsState,
    (_state: RootState, programIds: string[]) => normalizeProgramIds(programIds)
  ],
  (networkProgramsState, sortedKey) => {
    return networkProgramsState.statusByProgramIds[sortedKey] ?? "idle";
  }
);

export const selectNetworkProgramsError = createSelector(
  [
    selectNetworkProgramsState,
    (_state: RootState, programIds: string[]) => normalizeProgramIds(programIds)
  ],
  (networkProgramsState, sortedKey) => {
    return networkProgramsState.errorByProgramIds[sortedKey];
  }
);

export default networkProgramsSlice.reducer;
